import React, { useState } from "react";
import { Button, Typography, Box, IconButton, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Link from "@mui/material/Link";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import XIcon from "@mui/icons-material/X";
import GitHubIcon from "@mui/icons-material/GitHub";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ComingSoon from "../components/ComingSoon";

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonTitle, setComingSoonTitle] = useState("");
  const [comingSoonMessage, setComingSoonMessage] = useState("");

  const token = useSelector((state) => state.auth.token);

  const authPages = [
    "/auth/signup",
    "/auth/login",
    "/auth/forgot-password",
    "/auth/reset-password",
    "/auth/email-sent",
    "/auth/success",
  ];

  const showSignupCTA = !token && !authPages.includes(location.pathname);

  const handleClick = () => {
    navigate("/auth/signup");
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleComingSoon = (title, message) => {
    setComingSoonTitle(title);
    setComingSoonMessage(message);
    setComingSoonOpen(true);
  };

  const currentYear = new Date().getFullYear();

  return (
    <>
      <Box sx={{ backgroundColor: "#0d1f1c", color: "#fff", mt: "auto" }}>
        <Box sx={{ py: 6, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Stack
                spacing={2}
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      overflow: "hidden",
                      bgcolor: "#e8edf2",
                      flexShrink: 0,
                      border: "2px solid rgba(255,215,0,0.15)",
                    }}
                  >
                    <Box
                      component="img"
                      src="https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png"
                      alt="Le-Travaille"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#FFD700",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    Le-Travaille
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    maxWidth: "320px",
                    lineHeight: 1.8,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Your career journey starts here. Discover opportunities,
                  connect with employers, and build the future you deserve.
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  {[
                    {
                      icon: <LinkedInIcon fontSize="small" />,
                      label: "LinkedIn",
                      url: "https://www.linkedin.com",
                    },
                    {
                      icon: <XIcon fontSize="small" />,
                      label: "X",
                      url: "https://twitter.com",
                    },
                    {
                      icon: <GitHubIcon fontSize="small" />,
                      label: "GitHub",
                      url: "https://github.com",
                    },
                  ].map((social, index) => (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: "rgba(255,255,255,0.4)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        borderRadius: "8px",
                        p: 1,
                        "&:hover": {
                          color: "#FFD700",
                          borderColor: "rgba(255,215,0,0.2)",
                          backgroundColor: "rgba(255,215,0,0.05)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Stack>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} md={2}>
              <Stack
                spacing={2}
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#FFD700",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    fontSize: "0.75rem",
                  }}
                >
                  Explore
                </Typography>
                <Link
                  href="/jobs"
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Jobs
                </Link>
                <Link
                  href="/user/dashboard"
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Dashboard
                </Link>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "Companies",
                      "We're currently building our company directory. Soon you'll be able to browse and connect with top employers!",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Companies
                </Link>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "About",
                      "We're crafting our story to share with you. Learn more about our mission and values soon!",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  About
                </Link>
              </Stack>
            </Grid>

            <Grid item xs={6} md={2}>
              <Stack
                spacing={2}
                alignItems={{ xs: "center", md: "flex-start" }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#FFD700",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    fontSize: "0.75rem",
                  }}
                >
                  Support
                </Typography>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "Help Center",
                      "Our help center is under construction. We're creating comprehensive guides to help you make the most of Le-Travaille.",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Help Center
                </Link>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "Privacy Policy",
                      "We're finalizing our privacy policy to ensure your data is protected. Check back soon!",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "Terms of Service",
                      "Our terms of service are being drafted. We'll have them ready shortly.",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Terms of Service
                </Link>
                <Link
                  onClick={() =>
                    handleComingSoon(
                      "Contact",
                      "We're setting up our contact system. Soon you'll be able to reach out to us directly for support and inquiries.",
                    )
                  }
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "color 0.2s ease",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#FFD700",
                    },
                  }}
                >
                  Contact
                </Link>{" "}
              </Stack>
            </Grid>

            {showSignupCTA && (
              <Grid item xs={12} md={4}>
                <Stack
                  spacing={2}
                  alignItems={{ xs: "center", md: "flex-start" }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#FFD700",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      fontSize: "0.75rem",
                    }}
                  >
                    Get Started
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255,255,255,0.5)",
                      textAlign: { xs: "center", md: "left" },
                      fontSize: "0.9rem",
                    }}
                  >
                    Ready to take the next step in your career? Join thousands
                    of job seekers who found their dream job.
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={handleClick}
                    fullWidth
                    sx={{
                      backgroundColor: "#FFD700",
                      color: "#0d1f1c",
                      fontWeight: 700,
                      py: 1.2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                      transition:
                        "transform 0.2s ease, background-color 0.2s ease",
                    }}
                  >
                    Create Free Account →
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.04)",
            py: 3,
            px: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: { xs: "center", sm: "center" },
              position: "relative",
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.25)",
                textAlign: "center",
                flex: 1,
              }}
            >
              &copy; {currentYear} Le-Travaille. All rights reserved.
              <Box
                component="span"
                sx={{ display: { xs: "block", sm: "inline" } }}
              >
                {" "}
                · Made with ❤️ for job seekers
              </Box>
            </Typography>

            <IconButton
              size="small"
              onClick={scrollToTop}
              sx={{
                color: "rgba(255,255,255,0.3)",
                border: "1px solid rgba(255,255,255,0.04)",
                borderRadius: "8px",
                p: 0.5,
                position: { xs: "relative", sm: "absolute" },
                right: { xs: "auto", sm: 0 },
                "&:hover": {
                  color: "#FFD700",
                  borderColor: "rgba(255,215,0,0.2)",
                  backgroundColor: "rgba(255,215,0,0.05)",
                },
              }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <ComingSoon
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        title={comingSoonTitle}
        message={comingSoonMessage}
      />
    </>
  );
}

export default Footer;
